import { useMemo, useCallback } from "react"
import { useTheme, Box, Button, CircularProgress } from "@mui/material"
import TheatersIcon from "@mui/icons-material/Theaters"

import { AppNavigation } from "containers/AppNavigation"
import { Image } from "components/Image"
import { PageCard, PageCardContent } from "components/card"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { EmptyData } from "components/EmptyData"
import { useNavigate } from "react-router-dom"
import { MovieHeader } from "extensions/movie-show-time/components/MovieHeader"
import { TheatreShowtime } from "extensions/movie-show-time/components/TheatreShowtime"
import { useMovieDetailPage } from "extensions/movie-show-time/hooks/useMovieDetailPage"
import { get } from "lodash"
import { EXTENSION_NAME } from "extensions/movie-show-time/constants"
import { useExtension } from "hooks/extension/useExtension"

export const MovieDetailKioskHPage = ({ Layout = Box }) => {
  const navigate = useNavigate()
  const { extensionConfig } = useExtension()
  const { movie, movieId, isLoading, cinemaCode } = useMovieDetailPage()
  const cinemaConfig = useMemo(
    () => get(extensionConfig, EXTENSION_NAME, null),
    [extensionConfig]
  )
  const { title, genre = [], duration, image, theaters = [] } = movie
  const { logo: cinemaLogo, location } = useMemo(() => {
    const cinemas = get(cinemaConfig, "options.cinemas", [])
    const matchedCinemaConfig = cinemas.find(({ id }) => id === cinemaCode)
    return matchedCinemaConfig || {}
  }, [cinemaCode, cinemaConfig])

  const fallbackPoster = useMemo(() => {
    const cinemas = get(cinemaConfig, "options.cinemas", [])
    const matchedCinemaConfig = cinemas.find(({ id }) => id === cinemaCode)
    return get(matchedCinemaConfig, "fallback_poster", null)
  }, [cinemaCode, cinemaConfig])

  const theme = useTheme()
  const bannerStyleOverrides =
    theme.components[`AppBanner`]?.styleOverrides || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}

  const loadingstyleOverrides =
    theme.components[`MovieLoadingScreen`]?.styleOverrides || {}
  const { container } = loadingstyleOverrides

  const styleOverrides =
    theme.components[`MovieDetailPage`]?.styleOverrides || {}
  const {
    pageCardContent,
    headerLayout,
    bodyLayout,
    posterButtonContainer,
    theatreShowtimeLayout,
    emptyShowtimeWrapper,
    poster,
    button,
    movieDetailContainer,
  } = styleOverrides
  const { root: bannerRoot } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides
  const showtimeAvailable = theaters.length > 0

  const onClickCinemaLocation = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`)
    },
    [navigate]
  )

  return (
    <Layout
      aside={
        <>
          <PageCard>
            <Box sx={wrapper}>
              <Box sx={bannerRoot}></Box>
              <Box sx={body}>
                <AppNavigation />
              </Box>
              <Box sx={footer}>
                <NavigationControl />
              </Box>
            </Box>
          </PageCard>
        </>
      }
    >
      <PageCard>
        <PageCardContent sx={pageCardContent}>
          {isLoading ? (
            <Box sx={container}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <Box sx={movieDetailContainer}>
              <Box sx={headerLayout}>
                <MovieHeader
                  title={title}
                  logo={cinemaLogo}
                  genre={genre.join(" | ")}
                  duration={duration && `${duration} Min`}
                />
              </Box>
              <Box sx={bodyLayout}>
                <Box sx={posterButtonContainer}>
                  <Image src={image || fallbackPoster} sx={poster} />
                  <Button
                    variant="contained"
                    sx={button}
                    onClick={() => onClickCinemaLocation(location?.id)}
                  >
                    View location
                  </Button>
                </Box>
                <Box sx={theatreShowtimeLayout}>
                  {showtimeAvailable ? (
                    theaters.map(({ name, showtime, language, caption }) => (
                      <TheatreShowtime
                        key={`${movieId}-${name}`}
                        theatre={`THEATER ${name}`}
                        language={language}
                        caption={caption}
                        showtime={showtime}
                      />
                    ))
                  ) : (
                    <Box sx={emptyShowtimeWrapper}>
                      <EmptyData
                        icon={<TheatersIcon />}
                        primary={"Showtime Ended"}
                        secondary={"Check back tomorrow for new showtimes."}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
